/**
 * Media Utilities — Safe PiP and Recording Lifecycle
 *
 * Provides guarded PiP exit and comprehensive recording cleanup,
 * fixing the Android bug where PiP bubble persists after recording stops.
 */

/**
 * safeExitPiP — Guarded PiP exit that never throws.
 *
 * The unguarded `document.exitPictureInPicture()` call was crashing on
 * Android when called without a PiP element active. This wrapper checks
 * first and catches any errors.
 */
export const safeExitPiP = async (): Promise<void> => {
    try {
        if (document.pictureInPictureElement) {
            await document.exitPictureInPicture();
        }
    } catch (e) {
        // Non-fatal — PiP may have been dismissed by the user or the OS
        console.warn('[safeExitPiP]', e);
    }
};

/**
 * cleanupRecordingSession — Comprehensive recording teardown.
 *
 * Safely exits PiP, stops all media tracks, and clears stream references.
 * Should be called on:
 * - Normal recording stop
 * - Component unmount
 * - OS backgrounding (visibilitychange → 'hidden')
 * - Error during recording
 */
export const cleanupRecordingSession = async (
    screenStream: MediaStream | null,
    faceCamStream: MediaStream | null
): Promise<void> => {
    await safeExitPiP();
    screenStream?.getTracks().forEach(t => t.stop());
    faceCamStream?.getTracks().forEach(t => t.stop());
};

/**
 * createVisibilityHandler — Creates a handler for OS-level backgrounding.
 *
 * On Android, when the app is backgrounded mid-recording, the recording
 * should be saved and streams cleaned up. This creates the event handler
 * for use in useEffect.
 *
 * Usage in component:
 *   useEffect(() => {
 *     const handler = createVisibilityHandler({
 *       isRecording,
 *       screenStream: screenRef.current,
 *       faceCamStream: camRef.current,
 *       onCleanup: () => {
 *         setIsRecording(false);
 *         initiateUpload();
 *       },
 *     });
 *     document.addEventListener('visibilitychange', handler);
 *     return () => document.removeEventListener('visibilitychange', handler);
 *   }, [isRecording]);
 */
export const createVisibilityHandler = (opts: {
    isRecording: boolean;
    screenStream: MediaStream | null;
    faceCamStream: MediaStream | null;
    onCleanup: () => void;
}) => {
    return async () => {
        if (document.visibilityState === 'hidden' && opts.isRecording) {
            await cleanupRecordingSession(opts.screenStream, opts.faceCamStream);
            opts.onCleanup();
        }
    };
};

/**
 * fetchBlobWithProxy — Safe media downloading with CORS fallback
 *
 * Downloads media files from Firebase Storage. To avoid CORS issues:
 * 1. Try local Vite proxy first (in development)
 * 2. Try Firebase Storage SDK (if properly configured)
 * 3. Fallback to public CORS proxy
 */
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../services/firebaseConfig';

export const fetchBlobWithProxy = async (mediaUrl: string, timeoutMs: number = 300000): Promise<Blob> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const isLocal = import.meta.env.DEV;

    try {
        // 1. Local: Vite proxy (bypasses CORS in dev)
        if (isLocal && mediaUrl.includes('firebasestorage.googleapis.com')) {
            const fetchUrl = mediaUrl.replace('https://firebasestorage.googleapis.com', '/firebase-storage-proxy');
            try {
                const response = await fetch(fetchUrl, { signal: controller.signal });
                if (response.ok) {
                    const blob = await response.blob();
                    clearTimeout(timeoutId);
                    return blob;
                }
            } catch (proxyError: any) {
                if (proxyError.name === 'AbortError' || controller.signal.aborted) throw proxyError;
            }
        }

        // 2. Production: direct fetch against the signed URL.
        //    Reliable for large files (getBlob flakes >10MB with 200+ERR_FAILED).
        //    Requires CORS configured on the bucket.
        if (!isLocal && mediaUrl.includes('firebasestorage.googleapis.com')) {
            try {
                const response = await fetch(mediaUrl, { signal: controller.signal });
                if (response.ok) {
                    const blob = await response.blob();
                    clearTimeout(timeoutId);
                    return blob;
                }
            } catch (directError: any) {
                if (directError.name === 'AbortError' || controller.signal.aborted) throw directError;
                // Fall through to Firebase SDK
            }
        }

        // 3. Firebase SDK fallback — requires CORS configured on the Storage bucket
        const storagePathMatch = mediaUrl.match(/\/o\/(.+?)\?/);
        if (storagePathMatch) {
            const storagePath = decodeURIComponent(storagePathMatch[1]);
            const storageRef = ref(storage, storagePath);
            const blob = await getBlob(storageRef);
            if (controller.signal.aborted) throw new Error("Request timed out");
            clearTimeout(timeoutId);
            return blob;
        }

        // No recognizable Firebase Storage URL — attempt direct fetch
        const response = await fetch(mediaUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`Direct fetch failed: ${response.statusText}`);
        const blob = await response.blob();
        clearTimeout(timeoutId);
        return blob;

    } catch (error: any) {
        clearTimeout(timeoutId);
        const isTimeout = error.name === 'AbortError' || controller.signal.aborted;
        throw new Error(isTimeout
            ? `Request timed out (${timeoutMs / 1000}s)`
            : `Failed to fetch media: ${error.message}`);
    }
};
